import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Video, Plus, Link, Loader2, Users, Clock, ChevronRight, Trash2, Lock } from 'lucide-react'
import { MeetingLobby } from './MeetingLobby'
import { MeetingRoom } from './MeetingRoom'
import { useMeetingStore, useActiveMeetings, type ActiveMeeting } from '@/hooks'
import { useAuth } from '@/lib/auth/context'

// Persistent team room configuration
const TEAM_ROOM = {
  id: 'team-conference',
  roomId: 'team-conference-room',
  title: 'Team Conference Room',
  // Allowed users by email (lowercase)
  allowedUsers: [
    'john@mentatix.io',
    'kevin@mentatix.io',
    'nithin@mentatix.io',
    'ian@mentatix.io',
    // Add variants in case of different email domains
    'john@buyr.com',
    'kevin@buyr.com',
    'nithin@buyr.com',
    'ian@buyr.com',
  ],
}
import {
  generateMeetingToken,
  generateRoomId,
  generateRoomLink,
  generateParticipantIdentity,
  isLiveKitConfigured,
  getLiveKitUrl,
  useLiveKit,
} from '@/lib/livekit'

type View = 'home' | 'lobby' | 'meeting'

export function MeetingRoomsView() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useState<View>('home')
  const [joinRoomId, setJoinRoomId] = useState('')
  const [isJoining, setIsJoining] = useState(false)
  const [configError, setConfigError] = useState<string | null>(null)

  // Store
  const {
    roomId,
    displayName,
    showParticipants,
    setRoom,
    clearRoom,
    setDisplayName,
    setShowParticipants,
  } = useMeetingStore()

  // LiveKit context (persists across tab navigation)
  const {
    connect,
    disconnect,
    isConnected,
    participants,
    localParticipant,
    isMuted,
    isCameraOff,
    isScreenSharing,
    isBackgroundBlurEnabled,
    isBackgroundBlurLoading,
    blurStrength,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    toggleBackgroundBlur,
    setBlurStrength,
    switchAudioInput,
    switchVideoInput,
    switchAudioOutput,
    applyAudioSettings,
    error: roomError,
  } = useLiveKit()

  // Active public meetings tracking
  const {
    meetings: activeMeetings,
    isLoading: meetingsLoading,
    createMeeting,
    joinMeeting,
    leaveMeeting,
    forceDeleteAllMeetings,
  } = useActiveMeetings()

  // State for cleanup button
  const [isCleaningUp, setIsCleaningUp] = useState(false)

  const handleCleanup = async () => {
    setIsCleaningUp(true)
    // Use force delete to clear all stale meetings regardless of is_active status
    await forceDeleteAllMeetings()
    setIsCleaningUp(false)
  }

  // Track participant identity for database tracking
  const [participantIdentity, setParticipantIdentity] = useState<string | null>(null)

  // Get logged-in user for auto-populating display name
  const { adminUser } = useAuth()

  // Check if user can access team room
  const canAccessTeamRoom = adminUser?.email &&
    TEAM_ROOM.allowedUsers.includes(adminUser.email.toLowerCase())

  // Check if there are people in the team room
  const teamRoomMeeting = activeMeetings.find(m => m.roomId === TEAM_ROOM.roomId)
  const teamRoomParticipantCount = teamRoomMeeting?.participantCount || 0
  const teamRoomParticipants = teamRoomMeeting?.participants || []

  // Auto-populate display name from logged-in user
  useEffect(() => {
    if (adminUser) {
      // Build name from available parts, fall back to email prefix
      const userName = [adminUser.firstName, adminUser.lastName]
        .filter(Boolean)
        .join(' ') || adminUser.email?.split('@')[0] || ''
      // Only update if we have a real name (not "Dev Admin" fallback) or displayName is empty
      if (userName && (userName !== 'Dev Admin' || !displayName)) {
        setDisplayName(userName)
      }
    }
  }, [adminUser, setDisplayName])

  // Check LiveKit configuration on mount
  useEffect(() => {
    if (!isLiveKitConfigured()) {
      setConfigError('LiveKit is not configured. Please set VITE_LIVEKIT_URL, VITE_LIVEKIT_API_KEY, and VITE_LIVEKIT_API_SECRET in your .env file.')
    }
  }, [])

  // Handle room parameter from URL
  useEffect(() => {
    const roomParam = searchParams.get('room')
    if (roomParam && view === 'home') {
      setJoinRoomId(roomParam)
      handleJoinRoom(roomParam)
    }
  }, [searchParams])

  // Update view when connected
  useEffect(() => {
    if (isConnected && view === 'lobby') {
      setView('meeting')
    }
  }, [isConnected, view])

  // Clean up meeting when user closes tab or navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (roomId && participantIdentity && isConnected) {
        // Use sendBeacon for reliable cleanup on tab close
        const supabaseUrl = import.meta.env.VITE_NEXUS_SUPABASE_URL
        const supabaseKey = import.meta.env.VITE_NEXUS_SUPABASE_ANON_KEY

        if (supabaseUrl && supabaseKey) {
          // Mark participant as inactive using sendBeacon (fires even on tab close)
          const payload = JSON.stringify({
            roomId,
            participantIdentity,
          })

          navigator.sendBeacon(
            `${supabaseUrl}/functions/v1/meeting-leave`,
            new Blob([payload], { type: 'application/json' })
          )
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [roomId, participantIdentity, isConnected])

  const handleCreateRoom = () => {
    const newRoomId = generateRoomId()
    setRoom(newRoomId, `Meeting ${newRoomId}`)
    setView('lobby')

    // Update URL
    setSearchParams({ room: newRoomId })
  }

  const handleJoinRoom = (roomIdToJoin?: string) => {
    const id = roomIdToJoin || joinRoomId.trim()
    if (!id) return

    setRoom(id, `Meeting ${id}`)
    setView('lobby')

    // Update URL
    setSearchParams({ room: id })
  }

  const handleJoinMeeting = useCallback(async (deviceIds?: { audioDeviceId: string | null; videoDeviceId: string | null }) => {
    if (!roomId || !displayName.trim()) return

    setIsJoining(true)

    try {
      // Use a deterministic identity based on admin user ID to prevent duplicate participant records
      // This ensures re-joining updates the existing record instead of creating a new one
      const identity = adminUser?.id
        ? `admin_${adminUser.id}`
        : generateParticipantIdentity()
      const token = await generateMeetingToken(roomId, displayName, identity)
      const url = getLiveKitUrl()

      // Track participant identity for leave tracking
      setParticipantIdentity(identity)

      // Create/update meeting in database (public by default)
      await createMeeting(roomId, `Meeting ${roomId}`, true)

      // Pass device IDs to LiveKit (important for capture cards like Elgato)
      await connect(url, token, {
        audioDeviceId: deviceIds?.audioDeviceId,
        videoDeviceId: deviceIds?.videoDeviceId,
      })

      // Join meeting in database
      await joinMeeting(roomId, displayName, identity)
    } catch (err) {
      console.error('Failed to join meeting:', err)
    } finally {
      setIsJoining(false)
    }
  }, [roomId, displayName, connect, createMeeting, joinMeeting, adminUser])

  const handleLeaveMeeting = useCallback(async () => {
    // Track leave in database
    if (roomId && participantIdentity) {
      await leaveMeeting(roomId, participantIdentity)
    }

    disconnect()
    clearRoom()
    setParticipantIdentity(null)
    setView('home')
    setSearchParams({})
  }, [disconnect, clearRoom, setSearchParams, roomId, participantIdentity, leaveMeeting])

  // Show configuration error
  if (configError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <div className="max-w-md text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 flex items-center justify-center">
            <Video size={32} className="text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-[var(--text-main)]">Configuration Required</h2>
          <p className="text-[var(--text-muted)]">{configError}</p>
          <div className="p-4 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-left">
            <p className="text-sm text-[var(--text-muted)] mb-2">Add these to your .env file:</p>
            <code className="text-xs text-[var(--primary)]">
              VITE_LIVEKIT_URL=wss://your-server.com<br/>
              VITE_LIVEKIT_API_KEY=your_key<br/>
              VITE_LIVEKIT_API_SECRET=your_secret
            </code>
          </div>
        </div>
      </div>
    )
  }

  // Lobby view
  if (view === 'lobby' && roomId) {
    return (
      <MeetingLobby
        roomId={roomId}
        displayName={displayName}
        onDisplayNameChange={setDisplayName}
        onJoin={handleJoinMeeting}
        isJoining={isJoining}
      />
    )
  }

  // Meeting view
  if (view === 'meeting' && isConnected) {
    return (
      <MeetingRoom
        roomId={roomId || ''}
        participants={participants}
        localParticipant={localParticipant}
        isMuted={isMuted}
        isCameraOff={isCameraOff}
        isScreenSharing={isScreenSharing}
        isBackgroundBlurEnabled={isBackgroundBlurEnabled}
        isBackgroundBlurLoading={isBackgroundBlurLoading}
        blurStrength={blurStrength}
        showParticipants={showParticipants}
        onToggleMute={toggleMute}
        onToggleCamera={toggleCamera}
        onToggleScreenShare={toggleScreenShare}
        onToggleBackgroundBlur={toggleBackgroundBlur}
        onBlurStrengthChange={setBlurStrength}
        onToggleParticipants={() => setShowParticipants(!showParticipants)}
        onSwitchAudioInput={switchAudioInput}
        onSwitchVideoInput={switchVideoInput}
        onSwitchAudioOutput={switchAudioOutput}
        onApplyAudioSettings={applyAudioSettings}
        onLeave={handleLeaveMeeting}
      />
    )
  }

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'Just started'
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    return `${diffHours}h ago`
  }

  // Home view
  return (
    <div className="flex flex-col h-full p-8 animate-fade-in overflow-y-auto">
      <div className="max-w-2xl w-full mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[var(--primary)]/10 flex items-center justify-center mb-4">
            <Video size={32} className="text-[var(--primary)]" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--text-main)]">Meeting Rooms</h1>
          <p className="text-[var(--text-muted)] mt-2">
            Start or join a video meeting with anyone
          </p>
        </div>

        {/* Create new room */}
        <button
          onClick={handleCreateRoom}
          className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-[var(--primary)] hover:opacity-90 text-white font-medium rounded-xl transition-all"
        >
          <Plus size={20} />
          Create New Meeting
        </button>

        {/* Divider */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-[var(--border)]" />
          <span className="text-sm text-[var(--text-muted)]">or</span>
          <div className="flex-1 h-px bg-[var(--border)]" />
        </div>

        {/* Join existing room */}
        <div className="space-y-3">
          <div className="relative">
            <Link size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              type="text"
              value={joinRoomId}
              onChange={(e) => setJoinRoomId(e.target.value)}
              placeholder="Enter room ID or paste link"
              className="w-full bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-main)] pl-12 pr-4 py-4 rounded-xl focus:outline-none focus:border-[var(--primary)]"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleJoinRoom()
              }}
            />
          </div>
          <button
            onClick={() => handleJoinRoom()}
            disabled={!joinRoomId.trim()}
            className="w-full py-3 px-4 bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] disabled:opacity-50 disabled:cursor-not-allowed text-[var(--text-main)] font-medium rounded-xl transition-all border border-[var(--border)]"
          >
            Join Meeting
          </button>
        </div>

        {/* Room error */}
        {roomError && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {roomError}
          </div>
        )}

        {/* Team Conference Room - Only visible to specific users */}
        {canAccessTeamRoom && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Lock size={14} className="text-[var(--primary)]" />
              <h2 className="text-sm font-medium text-[var(--text-muted)]">Team Only</h2>
              {teamRoomParticipantCount > 0 && (
                <span className="text-xs text-red-400">
                  {teamRoomParticipantCount} {teamRoomParticipantCount === 1 ? 'person' : 'people'} inside
                </span>
              )}
            </div>
            <button
              onClick={() => handleJoinRoom(TEAM_ROOM.roomId)}
              className={`w-full p-4 rounded-xl transition-all text-left group ${
                teamRoomParticipantCount > 0
                  ? 'bg-gradient-to-r from-red-500/15 to-red-500/5 border border-red-500/40 hover:border-red-500 hover:from-red-500/25 hover:to-red-500/10'
                  : 'bg-gradient-to-r from-[var(--primary)]/10 to-[var(--primary)]/5 border border-[var(--primary)]/30 hover:border-[var(--primary)] hover:from-[var(--primary)]/20 hover:to-[var(--primary)]/10'
              }`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`relative w-10 h-10 rounded-full flex items-center justify-center ${
                    teamRoomParticipantCount > 0 ? 'bg-red-500/20' : 'bg-[var(--primary)]/20'
                  }`}>
                    <Video size={20} className={teamRoomParticipantCount > 0 ? 'text-red-400' : 'text-[var(--primary)]'} />
                    {/* Red glowing dot when people are inside */}
                    {teamRoomParticipantCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full animate-pulse shadow-[0_0_8px_2px_rgba(239,68,68,0.6)]" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[var(--text-main)]">
                      {TEAM_ROOM.title}
                    </h3>
                    <p className="text-sm text-[var(--text-muted)]">
                      {teamRoomParticipantCount > 0
                        ? `${teamRoomParticipants.map(p => p.displayName).join(', ')} ${teamRoomParticipantCount === 1 ? 'is' : 'are'} in the room`
                        : 'Private room for the core team'
                      }
                    </p>
                  </div>
                </div>
                <ChevronRight
                  size={20}
                  className={`transition-colors ${
                    teamRoomParticipantCount > 0
                      ? 'text-red-400 group-hover:text-red-300'
                      : 'text-[var(--text-muted)] group-hover:text-[var(--primary)]'
                  }`}
                />
              </div>
            </button>
          </div>
        )}

        {/* Available Meetings Section */}
        {activeMeetings.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <h2 className="text-lg font-semibold text-[var(--text-main)]">Live Meetings</h2>
                <span className="text-sm text-[var(--text-muted)]">({activeMeetings.length})</span>
              </div>
              <button
                onClick={handleCleanup}
                disabled={isCleaningUp}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)] rounded-lg transition-all disabled:opacity-50"
                title="Clean up stale meetings"
              >
                {isCleaningUp ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Clean up
              </button>
            </div>

            <div className="grid gap-3">
              {activeMeetings.map((meeting) => (
                <button
                  key={meeting.id}
                  onClick={() => handleJoinRoom(meeting.roomId)}
                  className="w-full p-4 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl hover:border-[var(--primary)] hover:bg-[var(--bg-hover)] transition-all text-left group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Video size={16} className="text-green-500 flex-shrink-0" />
                        <h3 className="font-medium text-[var(--text-main)] truncate">
                          {meeting.title}
                        </h3>
                      </div>
                      <p className="text-sm text-[var(--text-muted)] mb-2">
                        Hosted by {meeting.creatorName}
                      </p>

                      {/* Participants */}
                      <div className="flex items-center gap-4 text-xs text-[var(--text-muted)]">
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {meeting.participantCount} {meeting.participantCount === 1 ? 'person' : 'people'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {formatRelativeTime(meeting.createdAt)}
                        </span>
                      </div>

                      {/* Participant names */}
                      {meeting.participants && meeting.participants.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {meeting.participants.slice(0, 5).map((p) => (
                            <span
                              key={p.id}
                              className="px-2 py-0.5 bg-[var(--bg-element)] rounded-full text-xs text-[var(--text-secondary)]"
                            >
                              {p.displayName}
                            </span>
                          ))}
                          {meeting.participants.length > 5 && (
                            <span className="px-2 py-0.5 text-xs text-[var(--text-muted)]">
                              +{meeting.participants.length - 5} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <ChevronRight
                      size={20}
                      className="text-[var(--text-muted)] group-hover:text-[var(--primary)] transition-colors flex-shrink-0 mt-1"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
